import React, { forwardRef, useImperativeHandle, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../../../../components/Button/Button';
import { Modal, ModalRef } from '../../../../components/Modal/Modal';
import { TextField } from '../../../../components/TextField/TextField';
import { useLanguage } from '../../../../locale/useLanguage';

import './SaveAddressConfirmModal.css';

type SaveAddressConfirmModalProps = {
  onSave: (name: string) => void;
  onExit: () => void;
};

export const SaveAddressConfirmModal: React.ForwardRefExoticComponent<
  SaveAddressConfirmModalProps & React.RefAttributes<ModalRef>
> = forwardRef(({ onSave, onExit }, forwardedRef) => {
  const [t] = useLanguage();
  const modalRef = useRef<ModalRef>(null);
  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<{ name: string }>();

  useImperativeHandle(forwardedRef, () => ({
    openModal: modalRef.current!.openModal,
    closeModal: modalRef.current!.closeModal,
  }));

  const save = useMemo(() => {
    return handleSubmit(({ name }) => {
      onSave(name);
    });
  }, [handleSubmit, onSave]);

  return (
    <Modal ref={modalRef}>
      <h3 className="modal-title">
        {t('changeAddress.saveAddressWithNameModalTitle')}
      </h3>
      <div className="modal-description">
        <TextField
          {...register('name', {
            required: {
              value: true,
              message: t('changeAddress.saveAddressWithNameModalNameError'),
            },
          })}
          error={errors.name?.message}
          placeholder={t(
            'changeAddress.saveAddressWithNameModalNamePlaceholder'
          )}
        />
      </div>
      <div className="modal-button-container">
        <Button
          onClick={save}
          label={t('changeAddress.saveAddressWithNameModalSave')}
          isPrimary
        />
        <div className="change-address-confirm-modal--exit-button">
          <Button
            onClick={onExit}
            label={t('changeAddress.saveAddressWithNameModalClose')}
          />
        </div>
      </div>
    </Modal>
  );
});
